import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Edit, GripVertical, Save, X, Database, 
  UserCog, Users, ShoppingCart, FileText, ChevronDown, ChevronRight, Copy, Building2
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';

const FIELD_TYPES = [
  { value: 'text', label: 'Single Line Text' },
  { value: 'textarea', label: 'Paragraph Text' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'date', label: 'Date' },
  { value: 'price', label: 'Price (USD)' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkboxes' },
  { value: 'note', label: 'Notes / Instructions' }
];

const CATEGORY_CONFIG = {
  company_registration: {
    icon: Building2,
    label: 'Company Registration Questions',
    description: 'Questions for company/business registration',
    color: '#0D9488'
  },
  registration: { 
    icon: Users, 
    label: 'Registration Questions', 
    description: 'Questions for user registration during account creation',
    color: '#0EA5E9'
  },
  broker: { 
    icon: UserCog, 
    label: 'Broker Questions', 
    description: 'Questions for broker verification and onboarding',
    color: '#2B4B6A'
  },
  buyer: { 
    icon: ShoppingCart, 
    label: 'Buyer Questions', 
    description: 'Questions for buyer qualification',
    color: '#059669'
  },
  seller: { 
    icon: Users, 
    label: 'Seller Questions', 
    description: 'Questions for seller listing',
    color: '#7C3AED'
  }
};

const AdminQuestionTemplates = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('broker');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({ 
    company_registration: true,
    registration: true, 
    broker: true, 
    buyer: true, 
    seller: true
  });
  
  // Apply to Company state
  const [showApplyModal, setShowApplyModal] = useState(null); // category to apply
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [applying, setApplying] = useState(false);
  
  const [formData, setFormData] = useState({
    question_text: '',
    field_type: 'text',
    question_category: 'broker',
    options: [],
    optionsText: '',
    allow_multiple: false,
    is_required: true,
    display_order: 0,
    description: '',
    allow_document_upload: false
  });

  const isMainAdmin = user?.role === 'admin' || user?.role === 'main_admin';

  useEffect(() => {
    fetchData();
    if (isMainAdmin) {
      fetchCompanies();
    }
  }, [isMainAdmin]);

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/admin/companies');
      setCompanies(res.data.filter(c => !c.is_template));
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, templatesRes] = await Promise.all([
        api.get('/admin/question-templates/categories'),
        api.get('/admin/question-templates')
      ]);
      setCategories(categoriesRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('This will create default question templates. Continue?')) return;
    
    setSeeding(true);
    try {
      const res = await api.post('/admin/question-templates/seed-defaults');
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed templates');
    } finally {
      setSeeding(false);
    }
  };

  const handleApplyToCompany = async () => {
    if (!selectedCompany || !showApplyModal) {
      toast.error('Please select a company');
      return;
    }
    
    setApplying(true);
    try {
      const res = await api.post(`/admin/question-templates/apply-to-company/${selectedCompany}?category=${showApplyModal}`);
      toast.success(res.data.message);
      setShowApplyModal(null);
      setSelectedCompany('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply templates');
    } finally {
      setApplying(false);
    }
  };

  const handleOpenDialog = (template = null, category = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        question_text: template.question_text || '',
        field_type: template.field_type || 'text',
        question_category: template.question_category || 'broker',
        options: template.options || [],
        optionsText: (template.options || []).join('\n'),
        allow_multiple: template.allow_multiple || false,
        is_required: template.is_required !== false,
        display_order: template.display_order || 0,
        description: template.description || '',
        allow_document_upload: template.allow_document_upload || false,
        require_document_upload: template.require_document_upload || false
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        question_text: '',
        field_type: 'text',
        question_category: category || selectedCategory,
        options: [],
        optionsText: '',
        allow_multiple: false,
        is_required: true,
        display_order: 0,
        description: '',
        allow_document_upload: false,
        require_document_upload: false
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.question_text.trim()) {
      toast.error('Question text is required');
      return;
    }

    const payload = {
      ...formData,
      options: formData.field_type === 'select' || formData.field_type === 'checkbox'
        ? formData.optionsText.split('\n').filter(o => o.trim())
        : []
    };
    delete payload.optionsText;

    try {
      if (editingTemplate) {
        await api.put(`/admin/question-templates/${editingTemplate.id}`, payload);
        toast.success('Template updated');
      } else {
        await api.post('/admin/question-templates', payload);
        toast.success('Template created');
      }
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;

    try {
      await api.delete(`/admin/question-templates/${templateId}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const payload = {
        question_text: `${template.question_text} (Copy)`,
        field_type: template.field_type,
        question_category: template.question_category,
        options: template.options || [],
        allow_multiple: template.allow_multiple || false,
        is_required: template.is_required,
        display_order: template.display_order + 1,
        description: template.description || ''
      };
      await api.post('/admin/question-templates', payload);
      toast.success('Template duplicated');
      fetchData();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const handleDragEnd = async (result, category) => {
    if (!result.destination) return;
    
    const categoryTemplates = getTemplatesByCategory(category);
    const items = Array.from(categoryTemplates);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update display_order for all affected items
    const updates = items.map((item, index) => ({
      id: item.id,
      display_order: index + 1
    }));
    
    // Optimistically update the UI
    const newTemplates = templates.map(t => {
      const update = updates.find(u => u.id === t.id);
      return update ? { ...t, display_order: update.display_order } : t;
    });
    setTemplates(newTemplates);
    
    // Save to backend
    try {
      await Promise.all(
        updates.map(u => 
          api.post(`/admin/question-templates/${u.id}/reorder`, null, {
            params: { new_order: u.display_order }
          })
        )
      );
      toast.success('Order updated');
    } catch (error) {
      toast.error('Failed to update order');
      fetchData(); // Revert on error
    }
  };

  const getTemplatesByCategory = (category) => {
    return templates.filter(t => t.question_category === category).sort((a, b) => a.display_order - b.display_order);
  };

  // Separate core and custom templates
  const getCoreTemplates = (category) => {
    return templates.filter(t => t.question_category === category && t.is_core === true)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  };

  const getCustomTemplates = (category) => {
    return templates.filter(t => t.question_category === category && !t.is_core)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  };

  // Toggle required status for core questions
  const handleToggleRequired = async (template) => {
    try {
      await api.put(`/admin/question-templates/${template.id}`, {
        is_required: !template.is_required
      });
      toast.success(`"${template.question_text}" is now ${!template.is_required ? 'required' : 'optional'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update question');
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  if (!isMainAdmin) {
    return (
      <Sidebar>
        <div className="p-8 text-center">
          <p className="text-red-600">Access denied. Main Admin only.</p>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Question Templates</h1>
            <p className="text-gray-500 mt-1">
              Manage default question templates for registration, broker, buyer, and seller forms.
              These templates are copied to new companies.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {templates.length === 0 && (
              <button
                onClick={handleSeedDefaults}
                disabled={seeding}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50"
                data-testid="seed-defaults-btn"
              >
                <Database className="w-4 h-4" />
                {seeding ? 'Seeding...' : 'Seed Default Templates'}
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-orange-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Category Cards */}
            {Object.entries(CATEGORY_CONFIG).map(([category, config]) => {
              const coreTemplates = getCoreTemplates(category);
              const customTemplates = getCustomTemplates(category);
              const Icon = config.icon;
              const isExpanded = expandedCategories[category];
              
              return (
                <div key={category} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  {/* Category Header */}
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleCategory(category)}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${config.color}15` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: config.color }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{config.label}</h3>
                        <p className="text-sm text-gray-500">{config.description}</p>
                      </div>
                      <span className="ml-2 px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                        {coreTemplates.length > 0 
                          ? `${coreTemplates.length} core + ${customTemplates.length} custom`
                          : `${customTemplates.length} templates`
                        }
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {isMainAdmin && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowApplyModal(category); }}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                          title="Apply these templates to a company"
                        >
                          <Copy className="w-4 h-4" />
                          Apply to Company
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleOpenDialog(null, category); }}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white rounded-lg"
                        style={{ backgroundColor: config.color }}
                      >
                        <Plus className="w-4 h-4" />
                        Add
                      </button>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Questions List */}
                  {isExpanded && (
                    <div className="border-t border-gray-100">
                      {/* Core Questions Section - from database */}
                      {coreTemplates.length > 0 && (
                        <div className="bg-slate-50 border-b border-gray-200">
                          <div className="px-4 py-2 bg-slate-100 border-b border-gray-200">
                            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-2">
                              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                              Core System Fields (Click Required badge to toggle)
                            </p>
                          </div>
                          <div className="divide-y divide-gray-100">
                            {coreTemplates.map((template, idx) => (
                              <div 
                                key={template.id}
                                className="flex items-center justify-between p-4 bg-slate-50/50 hover:bg-slate-100/50"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="text-gray-300 cursor-not-allowed">
                                    <GripVertical className="w-4 h-4" />
                                  </div>
                                  <span className="text-xs font-mono text-gray-400 w-6">{idx + 1}.</span>
                                  <div>
                                    <p className="font-medium text-gray-700 flex items-center gap-2">
                                      {template.question_text}
                                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-normal">Core</span>
                                      {template.step && (
                                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded font-normal">
                                          {template.step}
                                        </span>
                                      )}
                                    </p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded">
                                        {FIELD_TYPES.find(f => f.value === template.field_type)?.label || template.field_type}
                                      </span>
                                      <button
                                        onClick={() => handleToggleRequired(template)}
                                        className={`text-xs px-2 py-0.5 rounded cursor-pointer hover:opacity-80 transition ${
                                          template.is_required 
                                            ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                        }`}
                                        title="Click to toggle required/optional"
                                      >
                                        {template.is_required ? 'Required' : 'Optional'}
                                      </button>
                                      {template.options?.length > 0 && (
                                        <span className="text-xs text-gray-500">
                                          {template.options.length} options
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleOpenDialog(template)}
                                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                    title="Edit options"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <span className="text-xs text-gray-400 italic">System field</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Custom Questions Header */}
                      {coreTemplates.length > 0 && (
                        <div className="px-4 py-2 bg-orange-50 border-b border-orange-200">
                          <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide flex items-center gap-2">
                            <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                            Custom Questions (Editable)
                          </p>
                        </div>
                      )}

                      {customTemplates.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                          <p>No custom templates yet. Click "Add" to create one.</p>
                        </div>
                      ) : (
                        <DragDropContext onDragEnd={(result) => handleDragEnd(result, category)}>
                          <Droppable droppableId={`droppable-${category}`}>
                            {(provided) => (
                              <div 
                                ref={provided.innerRef} 
                                {...provided.droppableProps}
                                className="divide-y divide-gray-100"
                              >
                                {customTemplates.map((template, idx) => (
                                  <Draggable key={template.id} draggableId={template.id} index={idx}>
                                    {(provided, snapshot) => (
                                      <div 
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={`flex items-center justify-between p-4 ${snapshot.isDragging ? 'bg-orange-50 shadow-lg' : 'hover:bg-gray-50'}`}
                                      >
                                        <div className="flex items-center gap-3">
                                          <div 
                                            {...provided.dragHandleProps}
                                            className="text-gray-400 cursor-grab active:cursor-grabbing hover:text-gray-600"
                                          >
                                            <GripVertical className="w-4 h-4" />
                                          </div>
                                          <span className="text-xs font-mono text-gray-400 w-6">{idx + 1}.</span>
                                          <div>
                                            <p className="font-medium text-gray-700">{template.question_text}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded">
                                                {FIELD_TYPES.find(f => f.value === template.field_type)?.label || template.field_type}
                                              </span>
                                              {template.is_required && (
                                                <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded">Required</span>
                                              )}
                                              {template.allow_document_upload && (
                                                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-600 rounded">Upload</span>
                                              )}
                                              {template.options?.length > 0 && (
                                                <span className="text-xs text-gray-500">{template.options.length} options</span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <button
                                            onClick={() => handleOpenDialog(template)}
                                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                          >
                                            <Edit className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={() => handleCopyTemplate(template)}
                                            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                                            title="Copy to another category"
                                          >
                                            <Copy className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={() => handleDelete(template.id)}
                                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </DragDropContext>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <h3 className="font-medium text-blue-800 mb-2">How Templates Work:</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>Drag & drop</strong> questions using the grip handle to reorder them</li>
            <li>• <strong>Duplicate</strong> questions using the copy icon to quickly create similar questions</li>
            <li>• Templates are copied to new companies when they are created</li>
            <li>• Each company can customize their questions after creation</li>
            <li>• Changes to templates don't affect existing company questions</li>
          </ul>
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingTemplate ? 'Edit Template' : 'Add Template'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question Text *</label>
                <input
                  type="text"
                  value={formData.question_text}
                  onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                  placeholder="Enter question..."
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.question_category}
                    onChange={(e) => setFormData({ ...formData, question_category: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="registration">Registration</option>
                    <option value="broker">Broker</option>
                    <option value="buyer">Buyer</option>
                    <option value="seller">Seller</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                  <select
                    value={formData.field_type}
                    onChange={(e) => setFormData({ ...formData, field_type: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {FIELD_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {(formData.field_type === 'select' || formData.field_type === 'checkbox') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Options (one per line)</label>
                  <textarea
                    value={formData.optionsText}
                    onChange={(e) => setFormData({ ...formData, optionsText: e.target.value })}
                    placeholder="Option 1&#10;Option 2&#10;Option 3"
                    rows={4}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              )}

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_required}
                    onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">Required</span>
                </label>
                {(formData.field_type === 'select' || formData.field_type === 'checkbox') && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.allow_multiple}
                      onChange={(e) => setFormData({ ...formData, allow_multiple: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700">Allow multiple</span>
                  </label>
                )}
              </div>

              <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.allow_document_upload}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      allow_document_upload: e.target.checked,
                      require_document_upload: e.target.checked ? formData.require_document_upload : false
                    })}
                    className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    data-testid="allow-document-upload-checkbox"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Allow Document Upload</span>
                    <p className="text-xs text-gray-500 mt-0.5">Users can upload files (ID, passport, certificates, etc.)</p>
                  </div>
                </label>
                
                {formData.allow_document_upload && (
                  <label className="flex items-center gap-3 cursor-pointer mt-3 ml-8 pt-3 border-t border-gray-200">
                    <input
                      type="checkbox"
                      checked={formData.require_document_upload}
                      onChange={(e) => setFormData({ ...formData, require_document_upload: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      data-testid="require-document-upload-checkbox"
                    />
                    <div>
                      <span className="text-sm font-medium text-red-700">Require Document</span>
                      <p className="text-xs text-gray-500 mt-0.5">Document upload is mandatory for this question</p>
                    </div>
                  </label>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Help text for this question..."
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={() => setShowDialog(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-5 py-2 bg-orange-600 text-white rounded-xl hover:bg-orange-700"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Apply to Company Modal */}
        {showApplyModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6">
              <div className="flex items-center gap-3 mb-6">
                <div 
                  className="p-3 rounded-xl"
                  style={{ backgroundColor: `${CATEGORY_CONFIG[showApplyModal]?.color || BRAND_BLUE}15` }}
                >
                  <Building2 className="w-6 h-6" style={{ color: CATEGORY_CONFIG[showApplyModal]?.color || BRAND_BLUE }} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Apply Templates to Company</h2>
                  <p className="text-sm text-gray-500">
                    {CATEGORY_CONFIG[showApplyModal]?.label}
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Company</label>
                  <select
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': BRAND_ORANGE }}
                    data-testid="select-company-modal"
                  >
                    <option value="">Choose a company...</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name} ({company.subdomain})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <div className="flex items-start gap-2">
                    <Database className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">Important</p>
                      <p>This will replace all existing {showApplyModal} questions for the selected company with the template questions.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowApplyModal(null);
                    setSelectedCompany('');
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyToCompany}
                  disabled={applying || !selectedCompany}
                  className="flex-1 px-4 py-2.5 text-white rounded-xl hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: CATEGORY_CONFIG[showApplyModal]?.color || BRAND_BLUE }}
                >
                  {applying ? 'Applying...' : 'Apply Templates'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminQuestionTemplates;
